// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Inputs/select: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PERSONAL_FIELDS = [
  "StudentID",
  "ApplicationID",
  "AdmissionDate",
  "Entry",
  "AcademicYear",
  "Qualification",
  "AdmissionType",
  "HostelType",
  "IsStudentActive",
  "CompletedYear",
  "Semester",
  "CampusCode",
  "SubCampusCode",
  "DegreeCode",
  "BranchCode",
  "FirstName",
  "FamilyName",
  "FullName",
  "Gender",
  "BloodGroup",
  "DateOfBirth",
  "AddressLine1",
  "AddressLine2",
  "City",
  "District",
  "State",
  "Zip",
  "AadharNumber",
  "PhoneNumber",
  "AlternatePhoneNumber",
  "EmailAddress",
  "Religion",
  "Caste",
  "SubCaste",
  "FatherFullName",
  "FatherAadharNumber",
  "FatherQualification",
  "FatherOccupation",
  "FatherPhoneNumber",
  "MotherFullName",
  "MotherAadharNumber",
  "MotherQualification",
  "MotherOccupation",
  "MotherPhoneNumber",
  "ReferenceName",
  "ReferencePhoneNumber",
  "PassportPhoto",
  "DocumentsVerified",
  "DocumentsStoredInfo",
  "DocumentsReturnedTo",
  "DocumentsReturnedOn",
  "DocumentsReturnedBy",
];

const EDUCATION_FIELDS = [
  "StudentID",
  "EducationLevel",
  "BoardType",
  "InstitutionName",
  "HallTicketNumber",
  "Percentage",
  "CGPA",
  "InstitutionCity",
  "InstitutionState",
  "PassedOutYear",
];

const EXAM_FIELDS = [
  "StudentID",
  "ExamType",
  "HallTicketNumber",
  "RankNo",
  "Score",
  "ExamYear",
];

const DOCUMENT_FIELDS = [
  "StudentID",
  "DocumentType",
  "IsReceived",
  "IsVerified",
  "VerifiedBy",
  "VerifiedDateTime",
  "Comments",
];
const FIELDS = PERSONAL_FIELDS;

const StudentPersonalInfoManager = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const selectSx = inputSx; // same border behavior

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [tab, setTab] = useState(0);
  const [newRow, setNewRow] = useState(() => {
    const base = {};
    FIELDS.forEach((f) => (base[f] = ""));
    return base;
  });
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loginCampusCode, setCampusCode] = useState("");
  const [searchId, setSearchId] = useState("");
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");
  
  const dateFields = ["AdmissionDate", "DateOfBirth", "DocumentsReturnedOn"];
  const datetimeFields = ["VerifiedDateTime"];

  const toDateValue = (value) => {
  
    if (!value) return null;
  
    const d = dayjs(value);
  
    return d.isValid() ? d : null;
  
  };

  const renderDatePicker = (value, onChange) => (
    <DatePicker
      format="YYYY-MM-DD"
      value={value ? dayjs(convertToISTDate(value)) : null}
      onChange={(newValue) => {
        onChange(newValue ? newValue.format("YYYY-MM-DD") : "");
      }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          sx: inputSx,
        },
      }}
    />
  );

  const downloadSampleExcel = () => {
    const cc = loginCampusCode;

    const makeRow = (headers) => {
      const r = {};
      headers.forEach((h) => (r[h] = ""));
      return r;
    };

    const personal1 = makeRow(PERSONAL_FIELDS);
    Object.assign(personal1, {
      CampusCode: cc,
      StudentID: "STU001",
      ApplicationID: "APP001",
      AdmissionDate: "2026-01-05",
      Entry: "Regular",
      AcademicYear: "2025-2026",
      Qualification: "Inter",
      AdmissionType: "Councelling",
      HostelType: "Day Scholar",
      IsStudentActive: "Active",
      Semester: "1",
      SubCampusCode: "KK",
      DegreeCode: "B.TECH",
      BranchCode: "CSE",
      FirstName: "Sai",
      FamilyName: "Kumar",
      FullName: "Kumar Sai",
      Gender: "M",
      BloodGroup: "O+",
      DateOfBirth: "2007-08-15",
      AddressLine1: "Near XYZ",
      City: "Kakinada",
      District: "Kakinada",
      State: "AP",
      Zip: "533001",
      AadharNumber: "123456789012",
      PhoneNumber: "9876543210",
      EmailAddress: "sai.kumar@example.com",
      DocumentsVerified: "Pending",
      DocumentsStoredInfo: "Rack A1",
    });

    const educationRows = [
      {
        StudentID: "STU001",
        EducationLevel: "Tenth",
        BoardType: "SSC",
        InstitutionName: "ABC High School",
        HallTicketNumber: "10TH12345",
        Percentage: 92.5,
        CGPA: "",
        InstitutionCity: "Kakinada",
        InstitutionState: "AP",
        PassedOutYear: "2023",
      },
      {
        StudentID: "STU001",
        EducationLevel: "Intermediate",
        BoardType: "BIEAP",
        InstitutionName: "XYZ Junior College",
        HallTicketNumber: "INT12345",
        Percentage: 88.75,
        CGPA: "",
        InstitutionCity: "Kakinada",
        InstitutionState: "AP",
        PassedOutYear: "2025",
      },
    ];

    const examRows = [
      {
        StudentID: "STU001",
        ExamType: "EAMCET",
        HallTicketNumber: "EAM12345",
        RankNo: "10000",
        Score: "",
        ExamYear: "2025",
      },
    ];

    const documentRows = [
      {
        StudentID: "STU001",
        DocumentType: "Tenth Certificate",
        IsReceived: 1,
        IsVerified: 1,
        VerifiedBy: "",
        VerifiedDateTime: "",
        Comments: "",
      },
      {
        StudentID: "STU001",
        DocumentType: "Inter Certificate",
        IsReceived: 1,
        IsVerified: 0,
        VerifiedBy: "",
        VerifiedDateTime: "",
        Comments: "Pending verification",
      },
    ];

    const wb = XLSX.utils.book_new();

    const addSheet = (name, rows, headers) => {
      const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
      ws["!cols"] = headers.map((h) => ({
        wch: Math.min(Math.max(h.length + 2, 14), 35),
      }));
      XLSX.utils.book_append_sheet(wb, ws, name);
    };

    addSheet("StudentPersonalInformation", [personal1], PERSONAL_FIELDS);
    addSheet("EducationDetails", educationRows, EDUCATION_FIELDS);
    addSheet("CompetitiveExams", examRows, EXAM_FIELDS);
    addSheet("DocumentDetails", documentRows, DOCUMENT_FIELDS);

    const instructions = [
      ["Sheet", "Purpose"],
      ["StudentPersonalInformation", "One row per student"],
      ["EducationDetails", "Multiple education rows per StudentID"],
      ["CompetitiveExams", "Multiple entrance exam rows per StudentID"],
      ["DocumentDetails", "Multiple document rows per StudentID"],
      ["Important", "StudentID connects child sheets to personal information"],
    ];

    const wsInfo = XLSX.utils.aoa_to_sheet(instructions);
    wsInfo["!cols"] = [{ wch: 30 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `StudentUpload_Sample_${cc}.xlsx`);
  };

  // Date helper (IST, YYYY-MM-DD)
  const convertToISTDate = (utcTime) => {
    if (!utcTime) return "";
    const d = new Date(utcTime);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  };

  // decode CampusCode from JWT and set into newRow
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setNewRow((r) => ({ ...r, CampusCode: cc }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, [token]);

  const fetchStudents = async (studentId = "") => {
    try {
      const res = await axios.get(`${API_URL}/api/Upload_StudentPersonalInformation`, {
        params: { StudentIDs: studentId.trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || []);
    } catch {
      setError("Failed to fetch student data");
    }
  };

  useEffect(() => {
    if (loginCampusCode) fetchStudents();
  }, [loginCampusCode]);

  const handleInputChange = (e, id = null) => {
    const { name, value } = e.target;
    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: value }));
    } else {
      setData((d) => d.map((row) => (row.id === id ? { ...row, [name]: value } : row)));
    }
  };

  const handleAddRow = async () => {
    try {
      const payload = { ...newRow, CampusCode: loginCampusCode };
      const { data: created } = await axios.post(`${API_URL}/api/Upload_StudentPersonalInformation`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => [...d, created]);

      setNewRow(() => {
        const base = {};
        FIELDS.forEach((f) => (base[f] = ""));
        base.CampusCode = loginCampusCode;
        return base;
      });

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add student personal info");
    }
  };

  const handleUpdateRow = async (id) => {
    const updated = data.find((r) => r.id === id);
    if (!updated) return;
    try {
      await axios.put(`${API_URL}/api/Upload_StudentPersonalInformation/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditRowId(null);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update student personal info");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/Upload_StudentPersonalInformation/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => d.filter((r) => r.id !== id));
      setSuccess(true);
    } catch {
      setError("Failed to delete record");
    }
  };

  const handleTabInputChange = (field, value, rowId = null) => {
    if (rowId == null) {
      setNewTabRow((r) => ({ ...r, [field]: value }));
      return;
    }

    setData((prev) =>
      prev.map((student) => {
        if (tab === 0) {
          return student.id === rowId ? { ...student, [field]: value } : student;
        }

        if (tab === 1) {
          return {
            ...student,
            DocumentDetails: (student.DocumentDetails || []).map((x) =>
              x.DocumentID === rowId ? { ...x, [field]: value } : x
            ),
          };
        }

        if (tab === 2) {
          return {
            ...student,
            EducationDetails: (student.EducationDetails || []).map((x) =>
              x.EducationDetailID === rowId ? { ...x, [field]: value } : x
            ),
          };
        }

        if (tab === 3) {
          return {
            ...student,
            CompetitiveExams: (student.CompetitiveExams || []).map((x) =>
              x.CompetitiveExamID === rowId ? { ...x, [field]: value } : x
            ),
          };
        }

        return student;
      })
    );
  };

  const handleAddTabRow = async () => {
    try {
      const apiBase = getApiBase();

      const payload = {
        ...newTabRow,
        CampusCode: loginCampusCode,
      };

      const res = await axios.post(`${API_URL}/api/${apiBase}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchStudents(searchId);
      setNewTabRow(getEmptyRow());
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add record");
    }
  };

  const handleUpdateTabRow = async (row) => {
    try {
      const apiBase = getApiBase();
      const idField = getIdField();
      const id = row[idField] || row.id;

      await axios.put(`${API_URL}/api/${apiBase}/${id}`, row, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setEditRowId(null);
      await fetchStudents(searchId);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update record");
    }
  };

  const handleDeleteTabRow = async (row) => {
    try {
      const apiBase = getApiBase();
      const idField = getIdField();
      const id = row[idField] || row.id;

      await axios.delete(`${API_URL}/api/${apiBase}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchStudents(searchId);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete record");
    }
  };  

  const handleBulkClick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", loginCampusCode);

    try {
      await axios.post(`${API_URL}/api/UploadStudentPersonalInformation`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchStudents(searchId);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload for student info failed");
    } finally {
      e.target.value = "";
    }
  };

  const handleSearch = async () => {
    await fetchStudents(searchId);
  };

  const getIsStudentActiveValue = (val) => {
    if (val === 1 || val === "1") return "Active";
    if (val === 0 || val === "0") return "InActive";
    return val ?? "";
  };

  const getActiveFields = () => {
    if (tab === 0) return PERSONAL_FIELDS;
    if (tab === 1) return DOCUMENT_FIELDS;
    if (tab === 2) return EDUCATION_FIELDS;
    if (tab === 3) return EXAM_FIELDS;
    return PERSONAL_FIELDS;
  };

  const getTabRows = () => {
    if (tab === 0) return data;

    if (tab === 1) {
      return data.flatMap((student) =>
        (student.DocumentDetails || []).map((doc) => ({
          ...doc,
          id: doc.DocumentID,
          StudentID: student.StudentID,
        }))
      );
    }

    if (tab === 2) {
      return data.flatMap((student) =>
        (student.EducationDetails || []).map((edu) => ({
          ...edu,
          id: edu.EducationDetailID,
          StudentID: student.StudentID,
        }))
      );
    }

    if (tab === 3) {
      return data.flatMap((student) =>
        (student.CompetitiveExams || []).map((exam) => ({
          ...exam,
          id: exam.CompetitiveExamID,
          StudentID: student.StudentID,
        }))
      );
    }

    return [];
  };

  const getApiBase = () => {
    if (tab === 0) return "StudentPersonalInformation";
    if (tab === 1) return "StudentDocuments";
    if (tab === 2) return "StudentEducationDetails";
    if (tab === 3) return "StudentCompetitiveExams";
  };

  const getIdField = () => {
    if (tab === 0) return "id";
    if (tab === 1) return "DocumentID";
    if (tab === 2) return "EducationDetailID";
    if (tab === 3) return "CompetitiveExamID";
  };

  const getEmptyRow = () => {
    const row = {};
    activeFields.forEach((f) => (row[f] = ""));
    row.StudentID = searchId || "";
    if (tab === 0) row.CampusCode = loginCampusCode;
    if (tab === 1) {
      row.IsReceived = 0;
      row.IsVerified = 0;
    }
    return row;
  };

  const [newTabRow, setNewTabRow] = useState({});  

  useEffect(() => {
    setEditRowId(null);
    setNewTabRow(getEmptyRow());
  }, [tab, searchId, loginCampusCode]);

  const activeFields = getActiveFields();
  const tabRows = getTabRows();

  return (
   <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Upload Student Information
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Search by Student ID, bulk upload via Excel, and edit records inline
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Search section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Search</Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  label="Enter Student ID"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ ...inputSx, minWidth: 260 }}
                />
                <Button variant="contained" sx={primaryBtnSx} onClick={handleSearch}>
                  Search
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: `1px solid ${BORDER}`,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 900 },
                "& .Mui-selected": { color: THEME_ORANGE },
                "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
              }}
            >
              <Tab label="Personal Information" />
              <Tab label="Document Details" />
              <Tab label="Education Details" />
              <Tab label="Competitive Exams" />
            </Tabs>
          </Paper>

          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>#</TableCell>
                      {activeFields.map((f) => (
                        <TableCell key={f}>{f}</TableCell>
                      ))}
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                    
                  <TableBody>
                    {/* ✅ Add row on TOP */}
                    <TableRow sx={zebraRowSx(0)}>
                      <TableCell/>

                      {activeFields.map((field) => (
                        <TableCell key={field}>
                          {field === "CampusCode" ? (
                            <TextField value={loginCampusCode} disabled size="small" fullWidth sx={inputSx} />
                          ) : field === "StudentID" && tab !== 0 ? (
                            <TextField
                              value={newTabRow.StudentID || searchId || ""}
                              onChange={(e) => handleTabInputChange("StudentID", e.target.value)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                            />
                          ) : dateFields.includes(field) ? (
                            renderDatePicker(newTabRow[field], (v) =>
                              handleTabInputChange(field, v)
                            )
                          ) : datetimeFields.includes(field) ? (
                            <TextField
                              type="datetime-local"
                              value={newTabRow[field] ? String(newTabRow[field]).slice(0, 16) : ""}
                              onChange={(e) => handleTabInputChange(field, e.target.value)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : field === "DocumentsVerified" ? (
                            <Select
                              value={newTabRow.DocumentsVerified || "Pending"}
                              onChange={(e) => handleTabInputChange("DocumentsVerified", e.target.value)}
                              size="small"
                              fullWidth
                              sx={selectSx}
                            >
                              <MenuItem value="Pending">Pending</MenuItem>
                              <MenuItem value="Verified">Verified</MenuItem>
                            </Select>
                          ) : field === "IsReceived" || field === "IsVerified" ? (
                            <Select
                              value={newTabRow[field] ?? 0}
                              onChange={(e) => handleTabInputChange(field, Number(e.target.value))}
                              size="small"
                              fullWidth
                              sx={selectSx}
                            >
                              <MenuItem value={1}>Yes</MenuItem>
                              <MenuItem value={0}>No</MenuItem>
                            </Select>
                          ) : field === "IsStudentActive" ? (
                            <Select
                              value={newTabRow.IsStudentActive ?? ""}
                              onChange={(e) => handleTabInputChange("IsStudentActive", e.target.value)}
                              size="small"
                              fullWidth
                              displayEmpty
                              sx={selectSx}
                            >
                              <MenuItem value="">
                                <em>Select</em>
                              </MenuItem>
                              <MenuItem value="Active">Active</MenuItem>
                              <MenuItem value="InActive">InActive</MenuItem>
                            </Select>
                          ) : (
                            <TextField
                              value={newTabRow[field] ?? ""}
                              onChange={(e) => handleTabInputChange(field, e.target.value)}
                              size="small"
                              fullWidth
                              sx={inputSx}
                            />
                          )}
                        </TableCell>

                      ))}
                          <TableCell>
                        <IconButton sx={iconBtnSx} onClick={handleAddTabRow} title="Add">
                          <AddIcon />
                        </IconButton>
                      </TableCell>
                    
                    </TableRow>
                    
                    {/* ✅ Existing rows below add row */}
                    {tabRows.map((row, idx) => {
                      const rowId =
                        tab === 0
                          ? row.id
                          : tab === 1
                          ? row.DocumentID
                          : tab === 2
                          ? row.EducationDetailID
                          : row.CompetitiveExamID;
                    
                      const isEditing = editRowId === rowId;
                    
                      return (
                        <TableRow key={`${tab}-${rowId || idx}`} sx={zebraRowSx(idx + 1)}>
                          <TableCell>{idx + 1}</TableCell>
                      
                          {activeFields.map((field) => (
                            <TableCell key={field}>
                              {isEditing ? (
                                field === "StudentID" || field === "CampusCode" ? (
                                  <TextField value={row[field] ?? ""} disabled size="small" fullWidth sx={inputSx} />
                                ) : dateFields.includes(field) ? (
                                  renderDatePicker(row[field], (v) =>
                                    handleTabInputChange(field, v, rowId)
                                  )
                                ) : datetimeFields.includes(field) ? (
                                  <TextField
                                    type="datetime-local"
                                    value={row[field] ? String(row[field]).slice(0, 16) : ""}
                                    onChange={(e) => handleTabInputChange(field, e.target.value, rowId)}
                                    size="small"
                                    fullWidth
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                ) : field === "DocumentsVerified" ? (
                                  <Select
                                    value={row.DocumentsVerified || "Pending"}
                                    onChange={(e) => handleTabInputChange("DocumentsVerified", e.target.value, rowId)}
                                    size="small"
                                    fullWidth
                                    sx={selectSx}
                                  >
                                    <MenuItem value="Pending">Pending</MenuItem>
                                    <MenuItem value="Verified">Verified</MenuItem>
                                  </Select>
                                ) : field === "IsReceived" || field === "IsVerified" ? (
                                  <Select
                                    value={row[field] ? 1 : 0}
                                    onChange={(e) => handleTabInputChange(field, Number(e.target.value), rowId)}
                                    size="small"
                                    fullWidth
                                    sx={selectSx}
                                  >
                                    <MenuItem value={1}>Yes</MenuItem>
                                    <MenuItem value={0}>No</MenuItem>
                                  </Select>
                                ) : field === "IsStudentActive" ? (
                                  <Select
                                    value={getIsStudentActiveValue(row.IsStudentActive)}
                                    onChange={(e) => handleTabInputChange(field, e.target.value, rowId)}
                                    size="small"
                                    fullWidth
                                    sx={selectSx}
                                  >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="InActive">InActive</MenuItem>
                                  </Select>
                                ) : (
                                  <TextField
                                    value={row[field] ?? ""}
                                    onChange={(e) => handleTabInputChange(field, e.target.value, rowId)}
                                    size="small"
                                    fullWidth
                                    sx={inputSx}
                                  />
                                )
                              ) : field === "AdmissionDate" ||
                                field === "DateOfBirth" ||
                                field === "DocumentsReturnedOn" ||
                                field === "VerifiedDateTime" ? (
                                row[field] ? convertToISTDate(row[field]) : ""
                              ) : field === "IsReceived" || field === "IsVerified" ? (
                                row[field] ? "Yes" : "No"
                              ) : (
                                row[field] ?? ""
                              )}
                            </TableCell>
                          ))}

                          <TableCell>
                            {isEditing ? (
                              <>
                                <IconButton sx={iconBtnSx} onClick={() => handleUpdateTabRow(row)} title="Save">
                                  <SaveIcon />
                                </IconButton>
                                <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                                  <CancelIcon />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton sx={iconBtnSx} onClick={() => setEditRowId(rowId)} title="Edit">
                                  <EditIcon />
                                </IconButton>
                                <IconButton sx={iconBtnSx} onClick={() => handleDeleteTabRow(row)} title="Delete">
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>

                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
   </LocalizationProvider> 
  );
};

export default StudentPersonalInfoManager;
